import { useEffect, useRef, useMemo, useState, useCallback } from 'react';

// Debounce delay for filter changes (ms)
// This prevents rapid filter toggles from causing race conditions
const FILTER_DEBOUNCE_DELAY = 300;

// Total animation duration: debounce + layout apply + camera animation
const FILTER_ANIMATION_TOTAL_DURATION = FILTER_DEBOUNCE_DELAY + 100 + 350; // ~750ms

/**
 * useAutoTidyOnFilterChange Hook
 * 
 * Automatically triggers layout and repositioning when filters change.
 * This ensures the flowchart doesn't have gaps from hidden tasks.
 * 
 * Behavior:
 * - When filter signature changes, triggers instant auto-layout (debounced)
 * - When exiting FOKUS mode with active filters, also triggers auto-layout
 * - Skips auto-tidy while in FOKUS mode (FOKUS handles its own layout)
 * - Uses debouncing to prevent race conditions when rapidly toggling filters
 * - Supports custom field filters in the filter signature
 * - Returns isAnimatingFilter state to block interactions during transitions
 * 
 * IMPORTANT: The debouncing is critical to prevent the "flying around" bug
 * that occurs when filters are toggled rapidly. Without debouncing, multiple
 * layout calculations can interfere with each other.
 */
export function useAutoTidyOnFilterChange({
  filters = {},
  showActionableOnly = false,
  isFocusMode,
  handleAutoLayoutInstant,
  handleGoToStart, // Animated version for smooth camera movement
  clearCustomPositions, // Clear runtime position overrides (so tasks fall back to saved positions)
  // Touched chains tracking - chains modified during filter mode get re-laid out
  touchedChainsRef = null, // Ref to Set of touched chain IDs
  taskToChainMap = null, // Map of taskId -> chainId
  tasks = [], // All tasks (for finding which tasks to clear positions for)
  onClearTouchedChainPositions = null, // Callback to clear saved positions for touched chain tasks
}) {
  // Track when filter animation is in progress (blocks interactions)
  const [isAnimatingFilter, setIsAnimatingFilter] = useState(false);
  const animationEndTimerRef = useRef(null);
  // Create a stable signature of the current filter state
  const filterSignature = useMemo(() => {
    const parts = [];
    const f = filters || {};
    if (f.status?.length) parts.push(`s:${[...f.status].sort().join(',')}`);
    if (f.category?.length) parts.push(`c:${[...f.category].sort().join(',')}`);
    if (f.tags?.length) parts.push(`t:${[...f.tags].sort().join(',')}`);
    if (f.priority?.length) parts.push(`p:${[...f.priority].sort().join(',')}`);
    if (f.dueDate?.length) parts.push(`d:${[...f.dueDate].sort().join(',')}`);
    if (f.missionType?.length) parts.push(`m:${[...f.missionType].sort().join(',')}`);
    if (f.difficulty?.length) parts.push(`diff:${[...f.difficulty].sort().join(',')}`);
    // Include custom field filters in signature
    if (f.customFields && Object.keys(f.customFields).length > 0) {
      const cfParts = Object.entries(f.customFields)
        .filter(([, criteria]) => {
          // Only include if criteria has actual values
          if (!criteria) return false;
          if (criteria.values?.length > 0) return true;
          if (criteria.search) return true;
          if (criteria.operator && criteria.value !== undefined && criteria.value !== '') return true;
          return false;
        })
        .map(([fieldId, criteria]) => {
          if (criteria.values?.length) return `${fieldId}:${[...criteria.values].sort().join(',')}`;
          if (criteria.search) return `${fieldId}:~${criteria.search}`;
          if (criteria.operator) return `${fieldId}:${criteria.operator}${criteria.value}`;
          return '';
        })
        .filter(Boolean)
        .sort()
        .join(';');
      if (cfParts) parts.push(`cf:${cfParts}`);
    }
    if (showActionableOnly) parts.push('actionable');
    return parts.join('|') || 'none'; // Use 'none' for empty filters to distinguish from initial state
  }, [filters, showActionableOnly]);
  
  const prevFilterSignatureRef = useRef(null); // Start as null to detect first real change
  const debounceTimerRef = useRef(null); // Debounce timer for filter changes
  const autoTidyTimerRef = useRef(null); // RAF timer for go-to-start
  const prevFocusModeRef = useRef(false); // Track previous focus mode state
  
  useEffect(() => {
    const prevSignature = prevFilterSignatureRef.current;
    const wasFocusMode = prevFocusModeRef.current;
    
    // Always update the refs
    prevFilterSignatureRef.current = filterSignature;
    prevFocusModeRef.current = isFocusMode;
    
    // Skip if this is the first render (prevSignature is null)
    if (prevSignature === null) {
      return;
    }
    
    // Don't auto-tidy if in focus mode (FOKUS handles its own layout)
    if (isFocusMode) {
      return;
    }
    
    // Check if we should trigger auto-tidy:
    // 1. Filter signature changed AND we have active filters, OR
    // 2. Just exited FOKUS mode and we have active filters
    const filterChanged = filterSignature !== prevSignature;
    const justExitedFokus = wasFocusMode && !isFocusMode;
    const hasActiveFilters = filterSignature !== 'none';
    
    // IMPORTANT: Only auto-tidy when we HAVE active filters
    // When filters are cleared (going back to 'none'), tasks should return to their saved positions
    // without any layout recalculation. The saved task.x/y values will be used automatically.
    // 
    // Recalculating layout when removing filters causes incorrect positioning because:
    // 1. The layout algorithm recomputes positions from scratch
    // 2. It doesn't preserve the user's manual arrangements
    // 3. Child nodes can end up at the same Y as parents (overlapping)
    const shouldAutoTidy = (filterChanged && hasActiveFilters) || (justExitedFokus && hasActiveFilters);
    
    // When filter is REMOVED (going from filtered to unfiltered), clear customPositions
    // This ensures tasks fall back to their saved task.x/y positions instead of
    // retaining the "compacted" positions from when the filter was active
    const filterRemoved = filterChanged && !hasActiveFilters && prevSignature !== 'none';
    
    if (filterRemoved && clearCustomPositions) {
      // Block interactions immediately - both via state AND direct DOM
      setIsAnimatingFilter(true);
      // IMMEDIATELY show blocker via DOM to prevent race conditions
      const blocker = document.querySelector('.flowchart__animation-blocker');
      if (blocker) blocker.style.display = 'block';
      
      if (animationEndTimerRef.current) {
        clearTimeout(animationEndTimerRef.current);
      }
      
      // Check if any chains were touched during filter mode
      const touchedChains = touchedChainsRef?.current;
      const hasTouchedChains = touchedChains && touchedChains.size > 0;
      
      if (hasTouchedChains && taskToChainMap && onClearTouchedChainPositions) {
        // Find all task IDs in touched chains
        const touchedTaskIds = [];
        taskToChainMap.forEach((chainId, taskId) => {
          if (touchedChains.has(chainId)) {
            touchedTaskIds.push(taskId);
          }
        });
        
        // Clear saved positions for these tasks so they get re-laid out
        if (touchedTaskIds.length > 0) {
          onClearTouchedChainPositions(touchedTaskIds);
        }
        
        // Clear the touched chains set for next filter session
        touchedChainsRef.current = new Set();
      }
      
      clearCustomPositions();
      // Still go to start position after clearing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          handleGoToStart?.();
          // Unblock interactions after camera animation completes
          animationEndTimerRef.current = setTimeout(() => {
            setIsAnimatingFilter(false);
            // Also hide blocker via DOM
            const blocker = document.querySelector('.flowchart__animation-blocker');
            if (blocker) blocker.style.display = '';
          }, 350); // Camera animation duration
        });
      });
      return;
    }
    
    if (!shouldAutoTidy) {
      return;
    }
    
    // Block interactions immediately when filter changes - both via state AND direct DOM
    setIsAnimatingFilter(true);
    // IMMEDIATELY show blocker via DOM to prevent race conditions
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker) blocker.style.display = 'block';
    
    if (animationEndTimerRef.current) {
      clearTimeout(animationEndTimerRef.current);
    }
    
    // Clear any existing timers to prevent race conditions
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (autoTidyTimerRef.current) {
      clearTimeout(autoTidyTimerRef.current);
      autoTidyTimerRef.current = null;
    }
    
    // Debounce the auto-layout to prevent rapid filter toggles from causing issues
    // This is critical to prevent the "flying around" bug
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      
      // Trigger auto-layout
      // Use INSTANT version to avoid animation - animated version doesn't work well
      // with filter changes because it only animates partial selections
      // 
      // IMPORTANT: skipSave=true because filter tidies should be visual-only
      // We don't want to overwrite the user's saved positions just because they filtered
      // When the filter is removed, tasks should return to their original positions
      handleAutoLayoutInstant({ skipSave: true });
      
      // Go to start position after the layout positions have been applied
      // handleAutoLayoutInstant has a setTimeout(50ms) internally to apply compacted positions,
      // so we need to wait longer than that before calculating the camera target
      autoTidyTimerRef.current = setTimeout(() => {
        handleGoToStart(); // Animated camera movement
        // Unblock interactions after camera animation completes
        animationEndTimerRef.current = setTimeout(() => {
          setIsAnimatingFilter(false);
          // Also hide blocker via DOM
          const blocker = document.querySelector('.flowchart__animation-blocker');
          if (blocker) blocker.style.display = '';
        }, 350); // Camera animation duration
        autoTidyTimerRef.current = null;
      }, 100); // Wait 100ms to ensure positions are fully applied
    }, FILTER_DEBOUNCE_DELAY);
    
    // Don't return cleanup - we want the timer to persist across re-renders
    // Note: touchedChainsRef and taskToChainMap are stable refs/memoized values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSignature, isFocusMode, handleAutoLayoutInstant, handleGoToStart, clearCustomPositions, onClearTouchedChainPositions]);
  
  // Cleanup timers on unmount only
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (autoTidyTimerRef.current) {
        clearTimeout(autoTidyTimerRef.current);
      }
      if (animationEndTimerRef.current) {
        clearTimeout(animationEndTimerRef.current);
      }
    };
  }, []);
  
  // Return the filter signature and animation state
  return { filterSignature, isAnimatingFilter };
}

export default useAutoTidyOnFilterChange;
